import type {
  AccommodationsByCounty,
  CapacityByYear,
  Granularity,
  Manifest,
  PeriodData,
  PeriodKey,
  Places,
  Settlement,
  TravelData,
  AgeData,
  EconomyData,
} from '@/types/data'

/**
 * Reads the JSON the pipeline generated into public/data.
 *
 * There is deliberately no call to ksh.hu here: both KSH endpoints refuse
 * cross-origin browser requests, so everything the page shows was fetched and
 * normalised at build time (SPEC.md §3).
 */

const BASE = `${import.meta.env.BASE_URL.replace(/\/$/, '')}/data`

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Nem sikerült betölteni: ${url} (HTTP ${response.status})`)
  }
  return (await response.json()) as T
}

export function loadManifest(): Promise<Manifest> {
  return getJson<Manifest>(`${BASE}/manifest.json`)
}

export function loadPeriod(granularity: Granularity, period: PeriodKey): Promise<PeriodData> {
  return getJson<PeriodData>(`${BASE}/${granularity}/${period}.json`)
}

export function loadTopology(name: 'hu-counties' | 'world'): Promise<unknown> {
  return getJson(`${BASE}/${name}.topo.json`)
}

export function loadSettlements(): Promise<Settlement[]> {
  return getJson<Settlement[]>(`${BASE}/settlements.json`)
}

export function loadAccommodationsByCounty(): Promise<AccommodationsByCounty> {
  return getJson<AccommodationsByCounty>(`${BASE}/accommodations-by-county.json`)
}

export function loadCapacity(): Promise<CapacityByYear> {
  return getJson<CapacityByYear>(`${BASE}/capacity.json`)
}

export function loadTravel(): Promise<TravelData> {
  return getJson<TravelData>(`${BASE}/travel.json`)
}

export function loadAges(): Promise<AgeData> {
  return getJson<AgeData>(`${BASE}/ages.json`)
}

export function loadEconomy(): Promise<EconomyData> {
  return getJson<EconomyData>(`${BASE}/economy.json`)
}

/**
 * Wikipedia-sourced descriptions, fetched only when someone actually opens
 * the "Látnivalók" panel — it is the one dataset here that nothing on screen
 * needs by default, so it stays out of the first paint. Memoised: the panel
 * is opened and closed a lot, the file changes once a month.
 */
let placesPromise: Promise<Places> | null = null
export function loadPlaces(): Promise<Places> {
  placesPromise ??= getJson<Places>(`${BASE}/places.json`)
  return placesPromise
}

/** Forget a failed load, so the panel's "Újra" button can actually retry. */
export function resetPlacesCache(): void {
  placesPromise = null
}
